import { useMemo, type ReactNode } from "react";

import { cn } from "@/lib/utils";

function pickRandomImage(images: readonly string[]): string {
  return images[Math.floor(Math.random() * images.length)];
}

export interface ErrorScreenLayoutProps {
  /** Image URLs; one is chosen at random on mount. */
  images: readonly string[];
  /** Body copy below the image. */
  message: string;
  /** Optional CTA row (e.g. Reload / Try again). */
  action?: ReactNode;
  /** Stretch to at least the viewport height (full-page error). */
  fullPage?: boolean;
  className?: string;
}

/**
 * Shared centered layout for branded error screens: image, short copy, optional
 * action. Individual screens (`SomethingWentWrong`, `NoInternetConnection`)
 * pass their own image set and messaging.
 */
export function ErrorScreenLayout({
  images,
  message,
  action,
  fullPage = false,
  className,
}: ErrorScreenLayoutProps) {
  const imageSrc = useMemo(() => pickRandomImage(images), [images]);

  return (
    <div
      role="alert"
      className={cn(
        "mx-auto flex w-full max-w-md flex-col items-center px-4 text-center",
        fullPage &&
          "min-h-dvh justify-center bg-sl-50 bg-[radial-gradient(ellipse_90%_60%_at_50%_-15%,rgba(135,79,255,0.14),transparent)]",
        !fullPage && "py-6",
        className,
      )}
    >
      <div className="mb-5 flex w-full max-w-[19rem] justify-center sm:max-w-[21rem]">
        <img
          src={imageSrc}
          alt=""
          draggable={false}
          className="pointer-events-none h-48 w-48 object-contain select-none"
        />
      </div>

      <p className="mx-auto max-w-[18rem] text-sm leading-relaxed font-semibold text-[#4D577C] sm:max-w-xs sm:text-base">
        {message}
      </p>

      {action ? <div className="mt-8">{action}</div> : null}
    </div>
  );
}

interface ErrorScreenPanelProps {
  children: ReactNode;
  className?: string;
}

/** Vertically centers an error screen inside a scrollable panel (e.g. My Shelf). */
export function ErrorScreenPanel({
  children,
  className,
}: ErrorScreenPanelProps) {
  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col overflow-y-auto px-3.5 pb-12 [scrollbar-width:none] md:px-6 [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      <div className="min-h-[2rem] flex-1" aria-hidden />
      <div className="shrink-0">{children}</div>
      <div className="min-h-[4rem] flex-1" aria-hidden />
    </div>
  );
}
